// Copyright ©️ 2026 eVolve MEP, LLC

import {
  type ChangeEvent,
  createContext,
  type FocusEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  type BendCreatorValidationRequest,
  type BendInputKey,
  type BendTypeName,
  type ConduitSizeId,
  type ConduitTypeId,
  type FacilityId,
  type MachineProfileId,
  type BenderProfile,
  bendInputKeys,
  type BendValidationResponse,
  extractErrorMessage,
  isCanceledApiRequest,
  type StrongExclude,
  useEvolveGet,
  useEvolvePost,
  type YBCRow,
} from '@evolvemep/api-ts';
import type { GetInputPropsOptions, GetInputPropsReturnType } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import { isKeyOf } from 'helpers/isKeyOf';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { strongEntries, strongFromEntries } from 'helpers/strongEntries';
import { useGeneralContext } from 'helpers/useGeneralContext';
import {
  getBendTypeWithYBCRows,
  getSuggestedPartName,
  withoutYbcRowErrors,
} from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/helpers';

import {
  type BendCreatorFormValues,
  bendInputDefaults,
  seedYbcRows,
  useBendCreatorFormContext,
} from './useBendCreatorFormContext';
import { useSelectBenderProfileForConduit } from './useSelectBenderProfileForConduit';

type YBCRowPath = `ybcRows.${number}.${keyof YBCRow}`;

/**
 * `getInputProps` for each field the backend validates. Unless noted, an input is read-only while a validation is in flight,
 * and validates when it loses focus.
 * Bend dimensions are keyed by their `BendInputKey`, and surface their clientside error on blur before validating.
 */
type FieldGetInputProps = {
  /** Not read-only while validating. A new shape clears the dimensions, YBC rows, errors, and validation from the previous one. */
  bendType: () => GetInputPropsReturnType;
  /** Validates on change, after picking a bender profile that supports the new material and size. */
  conduitTypeId: () => GetInputPropsReturnType;
  /** Validates on change, after picking a bender profile that supports the new material and size. */
  conduitSizeId: () => GetInputPropsReturnType;
  /** Validates on change. */
  benderProfileId: () => GetInputPropsReturnType;
  materialStockLength: () => GetInputPropsReturnType;
  /** Validates on change. Bind it to a checkbox or switch. */
  maximizeLegLength: () => GetInputPropsReturnType;
  /** A YBC row cell. Not read-only while validating. Surfaces its clientside error on blur, before validating. */
  ybcRows: (path: YBCRowPath) => GetInputPropsReturnType;
} & Record<BendInputKey, () => GetInputPropsReturnType>;

type BendCreatorValidationContextValue = {
  /** Validates the bend with the backend. */
  validateBend: <T extends StrongExclude<keyof BendCreatorFormValues, 'partName' | 'quantity'>>(
    /** Optional. If provided, indicates what field changed to trigger validation. */
    changingKey?: T,
    /** Optional. if provided, indicates the new value of the field that changed to trigger validation. */
    changingValue?: BendCreatorFormValues[T],
  ) => Promise<void>;
  fieldGetInputProps: FieldGetInputProps;
  isValidating: boolean;
  lastValidation: LastValidationState | null;
  /** Resets the validation state to null/not yet validated. Generally, if you did something that would invalidate the form (like blanking a field), you should call this. */
  resetLastValidation: () => void;
  currentProfile?: BenderProfile;
};

const BendCreatorValidationContextProvider = createContext<BendCreatorValidationContextValue | undefined>(undefined);

/**
 * This represents a backend error with a dictionary of field names to error messages.
 * If we get this kind of error we can use the values to set field errors on the form.
 */
type ServiceValidationError = {
  title: string;
  status: number;
  errors: Record<keyof BendCreatorValidationRequest, string[]>;
  traceId: string;
};

type LastValidationState = {
  request: BendCreatorValidationRequest;
} & (
  | {
      response: BendValidationResponse;
      isSuccess: true;
    }
  | {
      response: unknown;
      isSuccess: false;
    }
);

/**
 * If we have an HTTP400 error, assume it's a ServiceValidationError.
 */
const isServiceValidationError = (e: unknown): e is { response: { data: ServiceValidationError } } => {
  if (isNil(e)) return false;
  if (typeof e !== 'object') return false;
  return 'status' in e && e.status === 400;
};

export const BendCreatorValidationContext = ({
  children,
  validateOnMount,
  facilityId,
}: {
  children: ReactNode;
  validateOnMount?: boolean;
  /** The facility whose default bender profiles are preferred. Without one, defaults are skipped. */
  facilityId?: FacilityId | null;
}) => {
  const form = useBendCreatorFormContext();
  const {
    apiCall: requestValidation,
    loading: isValidating,
    abortAll,
  } = useEvolvePost('shop/fabrication/bendCreator/validate', {
    alertOnError: false,
    abortOnMoreRecent: true,
  });

  const [lastValidation, setLastValidation] = useState<LastValidationState | null>(null);
  const {
    values,
    getValues,
    setFieldValue,
    setValues,
    setFieldError,
    setErrors,
    clearFieldError,
    getInputProps,
    isValid,
    validateField,
  } = form;

  const prepareValues = useCallback(
    <T extends StrongExclude<keyof BendCreatorFormValues, 'partName' | 'quantity'>>(
      changingKey?: T,
      changingValue?: BendCreatorFormValues[T],
    ): BendCreatorValidationRequest | null => {
      const {
        partName: _,
        quantity: __,
        benderProfileId,
        conduitTypeId,
        conduitSizeId,
        bendType,
        materialStockLength,
        ybcRows,
        ...rest
      } = getValues();

      /* Don't try to perform server side validation until
       * required fields are present, some bend dimension value isn't blank, and the user's input is valid clientside. */
      if (
        isNil(benderProfileId) ||
        isNil(conduitTypeId) ||
        isNil(conduitSizeId) ||
        isNil(bendType) ||
        isNil(materialStockLength)
      ) {
        return null;
      }
      const bendTypeWithRows = getBendTypeWithYBCRows({ bendType, ybcRows });
      if (isNil(bendTypeWithRows)) return null;
      if (bendType === 'Custom') {
        if (!isValid('ybcRows')) return null;
      } else if (!bendInputKeys.some((key) => isNotNil(rest[key]))) {
        return null;
      }
      if (bendInputKeys.some((key) => !isValid(key))) return null;

      /* Some special case handling for angled bends, which can be overdefined.
       * Treat the possibly overdefined value that the user didn't touch as null.
       * It will be updated post-validation */
      if (bendType === 'Angled') {
        if (changingKey === 'kickHeight') {
          rest.centerToEnd1 = null;
        } else {
          rest.kickHeight = null;
        }
      }

      return {
        ...rest,
        benderProfileId,
        conduitTypeId,
        conduitSizeId,
        materialStockLength,
        ...bendTypeWithRows,
        /** If the changing key and value are provided, use them instead of what's in `rest`. */
        ...(isNotNil(changingKey) && isNotNil(changingValue) ? { [changingKey]: changingValue } : {}),
      };
    },
    [getValues, isValid],
  );

  // On success, update the form with values from the backend
  const handleSuccess = useCallback(
    (res: Awaited<ReturnType<typeof requestValidation>>) => {
      clearFieldError('bendType');
      // A success means the backend accepted every row, so a row error it set on an earlier request is stale.
      setErrors(withoutYbcRowErrors);
      res.updatedBend.forEach(({ keyName, value }) => {
        setFieldValue(keyName, value);
        clearFieldError(keyName);
      });
    },
    [setFieldValue, setErrors, clearFieldError],
  );

  // On error, if possible, update the form with errors, otherwise show a notification.
  const handleError = useCallback(
    (e: unknown) => {
      if (isServiceValidationError(e)) {
        const {
          response: {
            data: { errors },
          },
        } = e;
        // Put any errors that are not related to a given field on the "bend type" field.
        const generalErrors: string[] = [];
        strongEntries(errors).forEach(([key, errorMessages]) => {
          // The backend keys a row's errors as `ybcRows[1].angle`; the form path for that field is `ybcRows.1.angle`.
          const ybcRowPath = key.replace(/^ybcRows\[(\d+)\]/, 'ybcRows.$1');
          if (ybcRowPath.startsWith('ybcRows.')) {
            setFieldError(ybcRowPath, errorMessages.join(', '));
          } else if (isKeyOf(key, values)) {
            if (key === 'bendType') {
              generalErrors.push(...errorMessages);
            } else {
              setFieldError(key, errorMessages.join(', '));
            }
          } else {
            generalErrors.push(...errorMessages);
          }
        });
        if (generalErrors.length > 0) {
          setFieldError('bendType', generalErrors.join(', '));
        }
      } else {
        notifications.show({
          title: 'Error validating bend',
          message: `An unexpected error occurred while validating bend: ${extractErrorMessage(e)}`,
          color: 'red',
        });
      }
    },
    [values, setFieldError],
  );

  const resetLastValidation = useCallback(() => {
    // A request still in flight would otherwise land after the reset and restore a stale result.
    abortAll('Validation reset');
    setLastValidation(null);
  }, [abortAll]);

  /** Validate the bend and update the form with the results. */
  const validateBend = useCallback(
    async <T extends StrongExclude<keyof BendCreatorFormValues, 'partName' | 'quantity'>>(
      changingKey?: T,
      changingValue?: BendCreatorFormValues[T],
    ) => {
      const validationValues = prepareValues(changingKey, changingValue);
      if (isNil(validationValues)) {
        // Any previous result described a form state that no longer exists.
        resetLastValidation();
        return;
      }

      try {
        const res = await requestValidation(validationValues);
        handleSuccess(res);
        setLastValidation({ request: validationValues, response: res, isSuccess: true });
      } catch (e) {
        if (isCanceledApiRequest(e)) return;
        handleError(e);
        setLastValidation({ request: validationValues, response: e, isSuccess: false });
      }
    },
    [requestValidation, prepareValues, handleSuccess, handleError, resetLastValidation],
  );

  const shouldDoInitialValidation = useRef(validateOnMount ?? false);
  useEffect(() => {
    if (shouldDoInitialValidation.current) {
      void validateBend();
      shouldDoInitialValidation.current = false;
    }
  }, [validateBend]);

  // The profile id changes from the profile select, conduit changes, and restoring a bend from the cart.
  const { benderProfileId } = values;
  const currentProfileOpts = useMemo(
    () => ({ lazy: isNil(benderProfileId), abortOnMoreRecent: true }),
    [benderProfileId],
  );
  const { data: fetchedProfile, setDefaultOpts: setCurrentProfileOpts } = useEvolveGet(
    isNil(benderProfileId)
      ? 'shop/fabrication/machineProfile/:machineProfileId/bender'
      : `shop/fabrication/machineProfile/${benderProfileId}/bender`,
    currentProfileOpts,
  );
  useEffect(() => setCurrentProfileOpts(currentProfileOpts), [currentProfileOpts, setCurrentProfileOpts]);
  // `data` outlives the id it was fetched for, so it only counts as current while the ids match.
  const currentProfile =
    fetchedProfile?.machineProfile.machineProfileId === benderProfileId ? fetchedProfile : undefined;

  const handleBendTypeChange = useCallback(
    (bendType: BendTypeName | null) => {
      const { bendType: previousBendType, partName } = getValues();
      if (previousBendType === bendType) return;
      // Reset the dimension inputs and preview, and clear errors, since they could relate to the previous shape.
      setFieldValue('bendType', bendType);
      clearFieldError('bendType');
      bendInputKeys.forEach((key) => {
        setFieldValue(key, null);
        clearFieldError(key);
      });
      /* YBC rows and their errors belong to the previous selection, and Custom starts from two blank rows.
       * Custom has no leg to maximize and hides that switch, so it is turned off rather than carried over. */
      setFieldValue('ybcRows', bendType === 'Custom' ? seedYbcRows() : []);
      setErrors(withoutYbcRowErrors);
      if (bendType === 'Custom') setFieldValue('maximizeLegLength', false);
      if (isNotNil(bendType)) setFieldValue('partName', getSuggestedPartName(partName, previousBendType, bendType));
      resetLastValidation();
    },
    [getValues, setFieldValue, clearFieldError, setErrors, resetLastValidation],
  );

  const selectBenderProfileForConduit = useSelectBenderProfileForConduit(facilityId ?? null, currentProfile);
  const handleConduitChange = useCallback(
    async (change: { conduitTypeId: ConduitTypeId | null } | { conduitSizeId: ConduitSizeId | null }) => {
      setValues(change);
      const { conduitTypeId, conduitSizeId } = getValues();
      if (isNil(conduitTypeId) || isNil(conduitSizeId)) {
        resetLastValidation();
        return;
      }
      try {
        // A more recent conduit change is picking the profile and will validate once it has.
        if (!(await selectBenderProfileForConduit(conduitTypeId, conduitSizeId))) return;
      } catch (e) {
        if (isCanceledApiRequest(e)) return;
        notifications.show({
          title: 'Error selecting bender profile',
          message: `An unexpected error occurred while finding a compatible bender profile: ${extractErrorMessage(e)}`,
          color: 'red',
        });
      }
      await validateBend();
    },
    [setValues, getValues, resetLastValidation, selectBenderProfileForConduit, validateBend],
  );

  const handleBenderProfileChange = useCallback(
    (newMachineProfileId: MachineProfileId | null) => {
      setFieldValue('benderProfileId', newMachineProfileId);
      void validateBend('benderProfileId', newMachineProfileId);
    },
    [setFieldValue, validateBend],
  );

  const handleMaximizeLegLengthChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const isChecked = event.currentTarget.checked;
      setFieldValue('maximizeLegLength', isChecked);
      void validateBend('maximizeLegLength', isChecked);
    },
    [setFieldValue, validateBend],
  );

  /** `getInputProps` that is read-only while validating. A field with its own `onChange` validates there; any other validates on blur. */
  const getValidatedInputProps = useCallback(
    (
      path: StrongExclude<keyof BendCreatorFormValues, 'partName' | 'quantity' | 'ybcRows'>,
      {
        onChange,
        ...options
      }: GetInputPropsOptions & {
        /** Replaces the form's change handler. Its argument depends on the input: a value, or a checkbox's event. */
        onChange?: (valueOrEvent: never) => void;
      } = {},
    ) => {
      const { onBlur: internalOnBlur, ...rest } = getInputProps(path, options);
      const onBlur = (e: FocusEvent<HTMLInputElement>) => {
        // mantine use-form's typing upsets me.
        // oxlint-disable-next-line typescript/no-unsafe-call
        internalOnBlur?.(e);
        if (isNotNil(onChange)) return;
        /* Surface the clientside message for a bad bend dimension, which `validateBend` won't send to the backend.
         * Only when the value is invalid: `validateField` clears the field's error when its rule passes,
         * which would wipe an error the backend set. */
        if (isKeyOf(path, bendInputDefaults) && !isValid(path)) {
          validateField(path);
        }
        void validateBend(path);
      };
      return {
        ...rest,
        onBlur,
        ...(isNotNil(onChange) ? { onChange } : {}),
        ...(isValidating ? { readOnly: true } : {}),
      };
    },
    [isValidating, validateBend, getInputProps, isValid, validateField],
  );

  const fieldGetInputProps = useMemo<FieldGetInputProps>(
    () => ({
      // Not read-only while validating: a new shape discards the validation in flight.
      bendType: () => ({ ...getInputProps('bendType'), onChange: handleBendTypeChange }),
      conduitTypeId: () =>
        getValidatedInputProps('conduitTypeId', {
          onChange: (conduitTypeId: ConduitTypeId | null) => {
            void handleConduitChange({ conduitTypeId });
          },
        }),
      conduitSizeId: () =>
        getValidatedInputProps('conduitSizeId', {
          onChange: (conduitSizeId: ConduitSizeId | null) => {
            void handleConduitChange({ conduitSizeId });
          },
        }),
      benderProfileId: () => getValidatedInputProps('benderProfileId', { onChange: handleBenderProfileChange }),
      materialStockLength: () => getValidatedInputProps('materialStockLength'),
      maximizeLegLength: () =>
        getValidatedInputProps('maximizeLegLength', { type: 'checkbox', onChange: handleMaximizeLegLengthChange }),
      // Not read-only while validating: custom validation writes nothing back, and locking would swallow keystrokes.
      ybcRows: (path) => {
        const { onBlur: internalOnBlur, ...rest } = getInputProps(path);
        const onBlur = (e: FocusEvent<HTMLInputElement>) => {
          // mantine use-form's typing upsets me (onBlur is typed as `any`)
          // oxlint-disable-next-line typescript/no-unsafe-call
          internalOnBlur?.(e);
          if (!isValid(path)) validateField(path);
          void validateBend();
        };
        return { ...rest, onBlur };
      },
      ...strongFromEntries(bendInputKeys.map((key) => [key, () => getValidatedInputProps(key)] as const)),
    }),
    [
      getInputProps,
      getValidatedInputProps,
      handleBendTypeChange,
      handleConduitChange,
      handleBenderProfileChange,
      handleMaximizeLegLengthChange,
      isValid,
      validateField,
      validateBend,
    ],
  );

  const value = {
    validateBend,
    fieldGetInputProps,
    isValidating,
    lastValidation,
    resetLastValidation,
    currentProfile,
  };
  return <BendCreatorValidationContextProvider value={value}>{children}</BendCreatorValidationContextProvider>;
};

export const useBendCreatorValidationContext = () =>
  useGeneralContext(BendCreatorValidationContextProvider, 'BendCreatorValidationContext');
